/**
 * Picture of a signature on paper, made into a drawing like the one of the signature pad: white
 * becomes transparent, the signature is cut out and centred in the same size
 * (docs/architecture/signatures.md). Runs in the browser only; the image never leaves the device
 * before it is saved.
 */

import { SIGNATURE_HEIGHT, SIGNATURE_WIDTH } from '@nexus/ui';

/** Types every browser decodes; iPhones hand photos over as JPEG through the file dialog. */
export const SIGNATURE_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** Longest side the picture is scanned at: sharp enough for a signature, quick on a phone. */
const SCAN_SIZE = 1800;
/** Brightness from which a pixel counts as paper; down to `INK` it fades, so edges stay smooth. */
const PAPER = 205;
const INK = 150;
/** Pixels fainter than this do not count as part of the signature when cutting it out. */
const VISIBLE = 24;
/** Free space around the signature, as share of the height of the drawing. */
const MARGIN = 0.1;

export type SignatureUploadProblem = 'type' | 'size' | 'decode' | 'empty';

export class SignatureUploadError extends Error {
  constructor(readonly problem: SignatureUploadProblem) {
    super(problem);
    this.name = 'SignatureUploadError';
  }
}

/** Area of an image in pixels, `right` and `bottom` exclusive. */
export interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Makes light pixels transparent, in place: from `PAPER` on fully, between `INK` and `PAPER` in
 * part. Transparent pixels lose their colour too. Returns the area that still shows the
 * signature, `null` when nothing is left.
 */
export function clearBackground(image: {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}): Bounds | null {
  const { data, width, height } = image;
  let left = width;
  let top = height;
  let right = 0;
  let bottom = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const brightness = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
      const share =
        brightness >= PAPER ? 0 : brightness <= INK ? 1 : (PAPER - brightness) / (PAPER - INK);
      const alpha = Math.round(data[i + 3]! * share);
      data[i + 3] = alpha;
      if (alpha === 0) data.fill(0, i, i + 3);
      if (alpha >= VISIBLE) {
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x + 1);
        bottom = Math.max(bottom, y + 1);
      }
    }
  }
  return right > left ? { left, top, right, bottom } : null;
}

function canvas(width: number, height: number, options?: CanvasRenderingContext2DSettings) {
  const element = document.createElement('canvas');
  element.width = width;
  element.height = height;
  const context = element.getContext('2d', options);
  if (!context) throw new SignatureUploadError('decode');
  return { element, context };
}

/**
 * The signature on an uploaded picture as PNG data URL in the size of the signature pad. Throws
 * `SignatureUploadError` for a wrong type, a file over 10 MB, a picture the browser cannot decode
 * and one without anything dark on it.
 */
export async function signatureFromFile(file: File): Promise<string> {
  if (!SIGNATURE_UPLOAD_TYPES.includes(file.type)) throw new SignatureUploadError('type');
  if (file.size > MAX_UPLOAD_BYTES) throw new SignatureUploadError('size');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new SignatureUploadError('decode');
  }

  const scale = Math.min(1, SCAN_SIZE / Math.max(bitmap.width, bitmap.height));
  const scan = canvas(
    Math.max(1, Math.round(bitmap.width * scale)),
    Math.max(1, Math.round(bitmap.height * scale)),
    { willReadFrequently: true },
  );
  scan.context.drawImage(bitmap, 0, 0, scan.element.width, scan.element.height);
  bitmap.close();
  const pixels = scan.context.getImageData(0, 0, scan.element.width, scan.element.height);
  const bounds = clearBackground(pixels);
  if (!bounds) throw new SignatureUploadError('empty');
  scan.context.putImageData(pixels, 0, 0);

  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;
  const margin = SIGNATURE_HEIGHT * MARGIN;
  const fit = Math.min(
    (SIGNATURE_WIDTH - 2 * margin) / width,
    (SIGNATURE_HEIGHT - 2 * margin) / height,
  );
  const drawing = canvas(SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
  drawing.context.imageSmoothingQuality = 'high';
  drawing.context.drawImage(
    scan.element,
    bounds.left,
    bounds.top,
    width,
    height,
    (SIGNATURE_WIDTH - width * fit) / 2,
    (SIGNATURE_HEIGHT - height * fit) / 2,
    width * fit,
    height * fit,
  );
  return drawing.element.toDataURL('image/png');
}
